import React from 'react';
import { gql, useQuery } from '@apollo/client';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { OptionType, UserAccountRequestOptionEditor } from './UserAccountRequestOptionEditor';
import ProvidersManager from '@/pages/admin/settings/providers/ProvidersManager';

type UserAccountRequestOption = {
  id: number;
};

type OptionsQueryResponse = {
  roleOptions: UserAccountRequestOption[];
  organizationOptions: UserAccountRequestOption[];
  siteOptions: UserAccountRequestOption[];
};

const LIST_USER_ACCOUNT_REQUEST_OPTIONS = gql`
  query UserAccountRequestOptionsAdmin {
    roleOptions: userAccountRequestOptions(optionType: "role", includeInactive: true) {
      id
    }
    organizationOptions: userAccountRequestOptions(optionType: "organization", includeInactive: true) {
      id
    }
    siteOptions: userAccountRequestOptions(optionType: "site", includeInactive: true) {
      id
    }
  }
`;

type SettingsTab = 'providers' | OptionType;

const optionTypeMeta: Record<OptionType, { title: string; description: string }> = {
  role: {
    title: 'User Roles',
    description: 'Manage values shown in the Role dropdown on new account requests.'
  },
	  organization: {
	    title: 'Organization',
	    description: 'Manage organization values and default program access used during account provisioning.'
	  },
  site: {
    title: 'Sites',
    description: 'Manage values shown in the Site dropdown on new account requests.'
  }
};

type UserAccountRequestOptionsPageProps = {
  initialType?: SettingsTab | null;
};

export const UserAccountRequestOptionsPage: React.FC<UserAccountRequestOptionsPageProps> = ({
  initialType = 'providers'
}) => {
  const [activeTab, setActiveTab] = React.useState<SettingsTab>(initialType || 'providers');

  React.useEffect(() => {
    if (!initialType) return;
    setActiveTab(initialType);
  }, [initialType]);

  const { data, error } = useQuery<OptionsQueryResponse>(LIST_USER_ACCOUNT_REQUEST_OPTIONS, {
    fetchPolicy: 'cache-and-network'
  });

  const roleCount = Array.isArray(data?.roleOptions) ? (data?.roleOptions || []).length : 0;
  const organizationCount = Array.isArray(data?.organizationOptions) ? (data?.organizationOptions || []).length : 0;
  const siteCount = Array.isArray(data?.siteOptions) ? (data?.siteOptions || []).length : 0;

  const tabs: Array<{ id: SettingsTab; label: string; count?: number }> = [
    { id: 'providers', label: 'Provider Settings' },
    { id: 'organization', label: 'Organizations', count: organizationCount },
    { id: 'site', label: 'Sites', count: siteCount },
    { id: 'role', label: 'User Roles', count: roleCount }
  ];

  const activeMeta = activeTab === 'providers'
    ? {
      title: 'Provider Settings',
      description: 'Manage provider options and program assignments.'
    }
    : optionTypeMeta[activeTab];

  return (
    <div className="p-6 mx-auto space-y-4">
      <div className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Account Provisioning Settings</h2>
          <p className="mt-1 text-sm text-gray-600">
            Manage provider settings, organizations, sites, and user roles used by account requests.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 border-b border-gray-200">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`border-b-2 px-3 py-2 text-sm font-medium ${
                  isActive
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900'
                }`}
              >
                {tab.label}
                {typeof tab.count === 'number' && (
                  <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{tab.count}</span>
                )}
              </button>
            );
          })}
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}

        <div>
          <h3 className="text-base font-semibold text-gray-900">{activeMeta.title}</h3>
          <p className="mb-4 text-sm text-gray-600">{activeMeta.description}</p>
          {activeTab === 'providers' ? (
            <ProvidersManager embedded />
          ) : (
            <UserAccountRequestOptionEditor key={activeTab} optionType={activeTab} />
          )}
        </div>
      </div>
    </div>
  );
};

export const UserAccountRequestProviderSettingsPage: React.FC = () => {
  return <UserAccountRequestOptionsPage initialType="providers" />;
};

export const UserAccountRequestRolesSettingsPage: React.FC = () => {
  return <UserAccountRequestOptionsPage initialType="role" />;
};

export const UserAccountRequestOrganizationsSettingsPage: React.FC = () => {
  return <UserAccountRequestOptionsPage initialType="organization" />;
};

export const UserAccountRequestSitesSettingsPage: React.FC = () => {
  return <UserAccountRequestOptionsPage initialType="site" />;
};

export default UserAccountRequestOptionsPage;
